/**
 * AvatarPickerModal — browse user's avatars with filters and a Create button.
 * Delegates modal content for creating new avatars to NewAvatarModal.
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { Plus, Search, X } from "lucide-react";
import { MediaGridSkeleton } from "@/components/skeletons";
import { ugcStudioApi, type AvatarFilter, type Gender, type UgcAvatar } from "./api";
import { AvatarCard, FilterButton, GenderChip } from "./avatar-card";
import { NewAvatarModal } from "./new-avatar-modal";
import { EditAvatarModal } from "./edit-avatar-modal";

interface AvatarPickerModalProps {
  open: boolean;
  onClose: () => void;
  selectedId: string | null;
  onSelect: (avatar: UgcAvatar | null) => void;
}

export function AvatarPickerModal({ open, onClose, selectedId, onSelect }: AvatarPickerModalProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const qc = useQueryClient();
  const [filter, setFilter] = useState<AvatarFilter>("all");
  const [gender, setGender] = useState<Gender | undefined>(undefined);
  const [search, setSearch] = useState("");
  const [newAvatarOpen, setNewAvatarOpen] = useState(false);
  const [editAvatar, setEditAvatar] = useState<UgcAvatar | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["ugc-studio", "avatars", filter, gender],
    queryFn: () => ugcStudioApi.listAvatars(filter, gender),
    enabled: open,
  });

  const removeMut = useMutation({
    mutationFn: (id: string) => ugcStudioApi.deleteAvatar(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["ugc-studio", "avatars"] }),
  });

  const pinMut = useMutation({
    mutationFn: (avatar: UgcAvatar) =>
      ugcStudioApi.updateAvatar(avatar.id, { isPinned: !avatar.isPinned }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["ugc-studio", "avatars"] }),
  });

  const recreateMut = useMutation({
    mutationFn: (id: string) => ugcStudioApi.recreateSimilarAvatar(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["ugc-studio", "avatars"] });
      toast({ title: t("ugcStudio.avatar.similarGenerated") });
    },
    onError: (err: unknown) => {
      const msg = err instanceof Error ? err.message : t("ugcStudio.avatar.generationFailed");
      toast({ title: t("ugcStudio.avatar.errorTitle"), description: msg, variant: "destructive" });
    },
  });

  const avatars = (data?.avatars ?? []).filter((a) =>
    !search ? true : a.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={(o) => {
          if (!o) onClose();
        }}
      >
        <DialogContent className="bg-[#0a0c10] border-white/[0.08] max-w-5xl h-[80vh] flex flex-col">
          <div className="flex items-center gap-4 mb-4">
            <h2 className="text-white text-lg font-semibold">
              {t("ugcStudio.avatar.selectTitle")}
            </h2>
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t("ugcStudio.avatar.searchPlaceholder")}
                className="pl-9 bg-white/[0.04] border-white/[0.08] text-white"
              />
            </div>
            <Button variant="ghost" size="icon" onClick={onClose} className="text-white/50">
              <X className="w-4 h-4" />
            </Button>
          </div>

          <div className="flex gap-6 flex-1 min-h-0">
            <aside className="w-40 flex-shrink-0 space-y-1.5 text-sm">
              <FilterButton
                label="A  All"
                active={filter === "all"}
                onClick={() => setFilter("all")}
              />
              <FilterButton
                label="📌 Pinned"
                active={filter === "pinned"}
                onClick={() => setFilter("pinned")}
              />
              <FilterButton
                label={t("ugcStudio.avatar.myAvatars")}
                active={filter === "my"}
                onClick={() => setFilter("my")}
              />
              <div className="pt-3 text-white/40 text-xs uppercase tracking-wider">
                {t("ugcStudio.avatar.genderLabel")}
              </div>
              <div className="flex gap-1.5">
                <GenderChip
                  label="♂ M"
                  active={gender === "male"}
                  onClick={() => setGender(gender === "male" ? undefined : "male")}
                />
                <GenderChip
                  label="♀ K"
                  active={gender === "female"}
                  onClick={() => setGender(gender === "female" ? undefined : "female")}
                />
              </div>
            </aside>

            <div className="flex-1 overflow-y-auto">
              {isLoading ? (
                // VP-208: siatka kafelków w proporcjach avatarów (3/4) — ta sama
                // liczba kolumn co po załadowaniu, bez skoku wysokości modala.
                <MediaGridSkeleton
                  count={8}
                  aspect="aspect-[3/4]"
                  withFooter={false}
                  gridClassName="grid grid-cols-3 md:grid-cols-4 gap-3"
                />
              ) : (
                <div className="grid grid-cols-3 md:grid-cols-4 gap-3">
                  <button
                    onClick={() => setNewAvatarOpen(true)}
                    className="aspect-[3/4] flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-white/[0.12] hover:border-white/25 text-white/60 transition"
                  >
                    <Plus className="w-6 h-6" />
                    <span className="text-xs">{t("ugcStudio.avatar.createAvatar")}</span>
                  </button>
                  {avatars.map((av) => (
                    <AvatarCard
                      key={av.id}
                      avatar={av}
                      selected={selectedId === av.id}
                      onSelect={() => {
                        onSelect(av);
                        onClose();
                      }}
                      onPin={() => pinMut.mutate(av)}
                      onEdit={() => setEditAvatar(av)}
                      onRemove={() => {
                        if (confirm(t("ugcStudio.avatar.deleteConfirm", { name: av.name })))
                          removeMut.mutate(av.id);
                      }}
                      onRecreateSimilar={() => recreateMut.mutate(av.id)}
                      recreatePending={recreateMut.isPending && recreateMut.variables === av.id}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <NewAvatarModal
        open={newAvatarOpen}
        onClose={() => setNewAvatarOpen(false)}
        onCreated={() => {
          qc.invalidateQueries({ queryKey: ["ugc-studio", "avatars"] });
          toast({ title: t("ugcStudio.avatar.created") });
        }}
      />

      <EditAvatarModal
        open={!!editAvatar}
        avatar={editAvatar}
        onClose={() => setEditAvatar(null)}
        onSaved={() => qc.invalidateQueries({ queryKey: ["ugc-studio", "avatars"] })}
      />
    </>
  );
}
